/**
 * diffSnapshots — compute the delta between two PipelineSnapshots.
 *
 * Used by the (future) "Compare runs" admin widget so a user can answer:
 *  - "Did my T2.2 fix actually reduce blockers?"
 *  - "Which codes appeared after I touched the seed file?"
 *  - "Which questions flipped from PASS to FAIL?"
 *
 * Pure function. The diff is direction-aware: `base` is the older / left
 * snapshot, `target` is the newer / right snapshot. All numeric deltas are
 * computed as `target - base`.
 */

import type { GateVerdict, IssueDomain, IssueKind } from '../issueContract';
import type { SeverityLevel } from '../../utils/severity';
import type { PipelineSnapshot, SnapshotDiff } from './types';
function diffRecord<K extends string>(base: Record<K, number>, target: Record<K, number>, keys: K[]): Record<K, number> {
  const out = {} as Record<K, number>;
  for (const k of keys) {
    out[k] = (target[k] ?? 0) - (base[k] ?? 0);
  }
  return out;
}
const VERDICT_KEYS: GateVerdict[] = ['PASS', 'FAIL', 'REVIEW_NEEDED'];
const KIND_KEYS: IssueKind[] = ['blocker', 'coach', 'info'];
const SEVERITY_KEYS: SeverityLevel[] = ['critical', 'blocker', 'review', 'coach', 'info'];

// Keep in sync with IssueDomain in ../issues/codes.ts.
// HISTORY:
//  - P1-3 (mei 2026): diversity-stage issues were previously tagged under
//    domain 'support' (DIVERSITY shares stage 5 with SUPPORT). The fix gave
//    diversity its own domain so admin filters no longer lie.
//  - P1-2 (mei 2026): family-level content statistics (question count,
//    cito-spread, number-pattern collapse, linguistic-template collapse)
//    were previously bucketed under 'support'. Spun out to 'familyStats'
//    so the support tab no longer mixes coverage checks with family stats.
// Snapshots produced before these changes continue to count the affected
// issues under 'support'; they remain comparable because diffRecord defaults
// missing keys to 0.
const DOMAIN_KEYS: IssueDomain[] = ['schema', 'type', 'graph', 'content', 'pedagogy', 'visual', 'support', 'familyStats', 'diversity', 'seed', 'scoring', 'gate'];
export function diffSnapshots(base: PipelineSnapshot, target: PipelineSnapshot): SnapshotDiff {
  // Index rows for O(1) lookup
  const baseRows = new Map(base.rows.map(r => [r.qid, r]));
  const targetRows = new Map(target.rows.map(r => [r.qid, r]));

  // ── Verdict transitions ──────────────────────────────────────────────
  const verdictChanges: SnapshotDiff['verdictChanges'] = [];
  for (const [qid, t] of targetRows) {
    const b = baseRows.get(qid);
    if (!b) continue;
    if (b.v !== t.v) {
      verdictChanges.push({
        qid,
        fid: t.fid,
        mod: t.mod,
        from: b.v,
        to: t.v
      });
    }
  }

  // ── Questions only in one side ───────────────────────────────────────
  const questionsOnlyInBase: string[] = [];
  for (const qid of baseRows.keys()) {
    if (!targetRows.has(qid)) questionsOnlyInBase.push(qid);
  }
  const questionsOnlyInTarget: string[] = [];
  for (const qid of targetRows.keys()) {
    if (!baseRows.has(qid)) questionsOnlyInTarget.push(qid);
  }
  questionsOnlyInBase.sort();
  questionsOnlyInTarget.sort();

  // ── Code-frequency changes ───────────────────────────────────────────
  const baseCodeCounts = new Map<string, number>(base.codeFrequency.entries.map(e => [e.code, e.count]));
  const targetCodeCounts = new Map<string, number>(target.codeFrequency.entries.map(e => [e.code, e.count]));
  const allCodes = new Set<string>([...baseCodeCounts.keys(), ...targetCodeCounts.keys()]);
  const codeChanges: SnapshotDiff['codeChanges'] = [];
  for (const code of allCodes) {
    const b = baseCodeCounts.get(code) ?? 0;
    const t = targetCodeCounts.get(code) ?? 0;
    const delta = t - b;
    let status: SnapshotDiff['codeChanges'][number]['status'];
    if (b === 0 && t > 0) status = 'appeared';else if (t === 0 && b > 0) status = 'vanished';else if (delta > 0) status = 'increased';else if (delta < 0) status = 'decreased';else status = 'unchanged';
    codeChanges.push({
      code,
      base: b,
      target: t,
      delta,
      status
    });
  }
  // Sort: biggest absolute movers first
  codeChanges.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

  // ── Aggregate deltas ─────────────────────────────────────────────────
  const aggregate: SnapshotDiff['aggregate'] = {
    totalQuestions: target.aggregate.totalQuestions - base.aggregate.totalQuestions,
    totalIssues: target.aggregate.totalIssues - base.aggregate.totalIssues,
    avgScore: Math.round((target.aggregate.avgScore - base.aggregate.avgScore) * 10) / 10,
    verdicts: diffRecord(base.aggregate.verdicts, target.aggregate.verdicts, VERDICT_KEYS),
    severityCounts: diffRecord(base.aggregate.severityCounts, target.aggregate.severityCounts, SEVERITY_KEYS),
    issuesByKind: diffRecord(base.aggregate.issuesByKind, target.aggregate.issuesByKind, KIND_KEYS),
    issuesByDomain: diffRecord(base.aggregate.issuesByDomain, target.aggregate.issuesByDomain, DOMAIN_KEYS)
  };
  return {
    baseId: base.id,
    targetId: target.id,
    baseCreatedAt: base.createdAt,
    targetCreatedAt: target.createdAt,
    aggregate,
    verdictChanges,
    codeChanges,
    questionsOnlyInBase,
    questionsOnlyInTarget
  };
}